import { FC, useCallback } from "react";
import { useNavigate } from "react-router";

import { useLoosePermissionCheck } from "capitalrx-shell";
import { pricingLevelCreate } from "@/App/constants/permissions";
import { PricingLevelsTypes } from "@/types";

import { useCreatePricingLevel } from "../../api";
import { PricingLevelFormPage } from "../../components";
import { API_URL } from "../../constants";
import { PageContent } from "../../layouts";
import { useFailureHandler, useSuccessHandler } from "../../hooks";
import { formatOutgoingData, formatSubmittingData } from "../../utils";

const PricingLevelCreate: FC = () => {
  const allowed = useLoosePermissionCheck(pricingLevelCreate);
  const navigate = useNavigate();

  const failureHandler = useFailureHandler();
  const successHandler = useSuccessHandler({
    invalidates: [API_URL.PRICING_LEVEL_LIST],
    message: "The pricing level was added successfully",
    callback: (data) => {
      navigate(`/pricing-levels/${data?.id}/edit`);
    },
  });

  const createPricingLevel = useCreatePricingLevel({ onSuccess: successHandler, onError: failureHandler });

  const handlePricingLevelDetailsSubmit = useCallback(async (data: PricingLevelsTypes.PricingLevelBase): Promise<void> => {
    const formattedData = formatOutgoingData(data);
    createPricingLevel.mutateAsync(formatSubmittingData(formattedData));
  }, []);

  const handlePricingLevelDetailsCancel = useCallback((): void => {
    navigate("/pricing-levels");
  }, []);

  const title = "New Pricing Level";

  return (
    <PageContent pageTitle={title} canView={allowed}>
      <PricingLevelFormPage
        isEditMode={false}
        handleSubmit={handlePricingLevelDetailsSubmit}
        handleCancel={handlePricingLevelDetailsCancel}
      />
    </PageContent>
  );
};

export default PricingLevelCreate;
